import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { canAccess } from '../../constants/tiers';
import { toast } from 'sonner';
import {
  ChevronLeft, Lock, Loader2, MapPin, Users, Send, LogOut, UserPlus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 5000;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const InitialBubble = ({ name, size = 36 }) => {
  const initial = (name || '?').trim()[0]?.toUpperCase() || '?';
  return (
    <div
      className="rounded-full bg-[#2CA8A0] text-white font-semibold flex items-center justify-center shrink-0"
      style={{ width: size, height: size, fontSize: size / 2.5 }}
    >
      {initial}
    </div>
  );
};

/**
 * Peer Circles → Group Detail (iter 76 Phase 3).
 *
 * Header: photo (or sand placeholder), name, description, city, members row.
 * Action: Join (if not a member) / Leave (if a member).
 * Threaded board: chronological messages - members can post; non-members
 * see a "Join to post" prompt instead of the composer. Polls every 5 s.
 */
const PortalPeerCirclesGroupDetail = () => {
  const { groupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const unlocked = canAccess(user?.role, 'peerCircles');

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [busyJoin, setBusyJoin] = useState(false);
  const bottomRef = useRef(null);

  const fetchGroup = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/peer-circles/groups/${groupId}`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail?.message || d.detail || 'Group not found');
      }
      setGroup(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/peer-circles/groups/${groupId}/messages`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (!res.ok) return;
      const d = await res.json();
      setMessages(d.messages || []);
    } catch { /* noop */ }
  }, [groupId]);

  useEffect(() => {
    if (!unlocked || !groupId) return undefined;
    fetchGroup();
    fetchMessages();
    return undefined;
  }, [unlocked, groupId, fetchGroup, fetchMessages]);

  // Poll messages every 5s
  useEffect(() => {
    if (!unlocked || !groupId) return undefined;
    const t = setInterval(fetchMessages, POLL_MS);
    return () => clearInterval(t);
  }, [unlocked, groupId, fetchMessages]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  const handleJoin = async () => {
    setBusyJoin(true);
    try {
      const res = await fetch(`${API_URL}/api/peer-circles/groups/${groupId}/join`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Could not join.');
      toast.success(`Joined ${group?.name}.`);
      await fetchGroup();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyJoin(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm(`Leave ${group?.name}?`)) return;
    setBusyJoin(true);
    try {
      const res = await fetch(`${API_URL}/api/peer-circles/groups/${groupId}/leave`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Could not leave.');
      toast.success(`Left ${group?.name}.`);
      navigate('/portal/peer-circles/groups');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyJoin(false);
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/peer-circles/groups/${groupId}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ body: text }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail?.message || data.detail || 'Could not post.');
      setMessages((cur) => [...cur, data.message]);
      setDraft('');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  if (!unlocked) {
    return (
      <PortalLayout activeTab="peerCircles">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-14" data-testid="group-detail-locked">
          <div className="bg-white border border-[#EFE6CF] rounded-2xl p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#F4ECD8] flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-[#1A5C6E]" />
            </div>
            <h1 className="font-display italic text-3xl text-[#0A1F2E] mb-2">Groups are part of Peer Circles</h1>
            <p className="text-[#0A1F2E]/70 mb-6">Upgrade to join interest groups and city crews.</p>
            <Link
              to="/portal/upgrade"
              className="inline-flex items-center gap-1 bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-6 py-3 rounded-full transition"
              data-testid="group-detail-upgrade-cta"
            >
              See plans
            </Link>
          </div>
        </div>
      </PortalLayout>
    );
  }

  if (loading) {
    return (
      <PortalLayout activeTab="peerCircles">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
        </div>
      </PortalLayout>
    );
  }

  if (error || !group) {
    return (
      <PortalLayout activeTab="peerCircles">
        <div className="max-w-3xl mx-auto p-6 lg:p-8">
          <div className="bg-[#B91C1C]/10 border border-[#B91C1C]/30 text-[#B91C1C] p-4 rounded-xl text-sm" data-testid="group-detail-error">
            {error || 'Group not found'}
          </div>
          <Link
            to="/portal/peer-circles/groups"
            className="mt-4 inline-flex items-center gap-1 text-[#1A5C6E] hover:text-[#0A1F2E] text-sm font-mono uppercase tracking-[0.12em] text-[11px]"
          >
            <ChevronLeft className="w-4 h-4" /> Back to groups
          </Link>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout activeTab="peerCircles">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-8 md:py-10" data-testid="group-detail-page">
        <button
          type="button"
          onClick={() => navigate('/portal/peer-circles/groups')}
          className="inline-flex items-center gap-1 text-[#1A5C6E] hover:text-[#0A1F2E] text-sm mb-6 transition font-mono uppercase tracking-[0.12em] text-[11px]"
          data-testid="group-detail-back"
        >
          <ChevronLeft className="w-4 h-4" /> All groups
        </button>

        {/* Header */}
        <section className="bg-white border border-[#EFE6CF] rounded-2xl overflow-hidden mb-5" data-testid="group-detail-header">
          {group.photo_url ? (
            <img src={group.photo_url} alt="" className="w-full h-48 sm:h-56 object-cover" />
          ) : (
            <div className="w-full h-40 bg-[#F4ECD8] flex items-center justify-center">
              <Users className="w-12 h-12 text-[#1A5C6E]/45" />
            </div>
          )}
          <div className="p-5 sm:p-6">
            <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
              Peer Circles · Group
            </p>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h1 className="font-display italic text-3xl md:text-4xl text-[#0A1F2E] leading-tight break-words" data-testid="group-detail-name">
                  {group.name}
                </h1>
                <div className="flex items-center gap-4 mt-2 text-sm text-[#0A1F2E]/60">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {group.city}</span>
                  <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {group.member_count} members</span>
                </div>
                <p className="text-[#0A1F2E]/80 mt-4 leading-relaxed whitespace-pre-line break-words" data-testid="group-detail-description">
                  {group.description}
                </p>
              </div>
              <div className="shrink-0">
                {group.is_member ? (
                  <button
                    type="button"
                    disabled={busyJoin}
                    onClick={handleLeave}
                    className="inline-flex items-center gap-1.5 bg-white border border-[#0A1F2E]/20 hover:border-[#B91C1C]/50 hover:text-[#B91C1C] disabled:opacity-50 text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2 rounded-full transition"
                    data-testid="group-leave-btn"
                  >
                    <LogOut className="w-3.5 h-3.5" /> Leave group
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busyJoin}
                    onClick={handleJoin}
                    className="inline-flex items-center gap-1.5 bg-[#0A1F2E] hover:bg-[#163448] disabled:opacity-50 text-white font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2 rounded-full transition"
                    data-testid="group-join-btn"
                  >
                    <UserPlus className="w-3.5 h-3.5" /> Join group
                  </button>
                )}
              </div>
            </div>

            {/* Members strip */}
            {group.members && group.members.length > 0 && (
              <div className="mt-5 pt-5 border-t border-[#EFE6CF]" data-testid="group-members-strip">
                <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 mb-3">Members</p>
                <div className="flex flex-wrap gap-2">
                  {group.members.slice(0, 24).map((m) => (
                    <Link
                      key={m.user_id}
                      to={`/portal/members/${m.user_id}`}
                      className="flex items-center gap-2 bg-[#F9F6EF] hover:bg-[#F4ECD8] border border-[#EFE6CF] rounded-full pl-1 pr-3 py-1 transition"
                      title={`${m.first_name} ${m.last_name_initial}.`}
                      data-testid={`group-member-${m.user_id}`}
                    >
                      {m.profile_photo_url ? (
                        <img src={m.profile_photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <InitialBubble name={m.first_name} size={24} />
                      )}
                      <span className="text-xs text-[#0A1F2E]">{m.first_name} {m.last_name_initial}.</span>
                    </Link>
                  ))}
                  {group.members.length > 24 && (
                    <span className="text-xs text-[#0A1F2E]/55 self-center">+{group.members.length - 24} more</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Threaded board */}
        <section className="bg-white border border-[#EFE6CF] rounded-2xl overflow-hidden" data-testid="group-board">
          <header className="px-5 py-3 border-b border-[#EFE6CF]">
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">Board</p>
          </header>

          <div className="p-4 sm:p-5 space-y-4 bg-[#F9F6EF] max-h-[480px] overflow-y-auto">
            {messages.length === 0 ? (
              <p className="text-center text-[#0A1F2E]/55 text-sm py-8">No posts yet - be the first.</p>
            ) : (
              messages.map((m) => (
                <article
                  key={m.message_id}
                  className="bg-white border border-[#EFE6CF] rounded-xl p-4 flex items-start gap-3"
                  data-testid={`group-msg-${m.message_id}`}
                >
                  <Link to={`/portal/members/${m.sender?.user_id}`} className="shrink-0">
                    {m.sender?.profile_photo_url ? (
                      <img src={m.sender.profile_photo_url} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-[#EFE6CF]" />
                    ) : (
                      <InitialBubble name={m.sender?.first_name} size={36} />
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/portal/members/${m.sender?.user_id}`}
                        className="font-display italic text-[#0A1F2E] hover:text-[#1A5C6E]"
                      >
                        {m.sender?.first_name || 'Member'}{m.sender?.last_name_initial ? ` ${m.sender.last_name_initial}.` : ''}
                      </Link>
                      <span className="text-[10px] text-[#0A1F2E]/45">
                        {new Date(m.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago'})}
                      </span>
                    </div>
                    <p className="text-sm text-[#0A1F2E] mt-1 leading-relaxed whitespace-pre-line break-words">{m.body}</p>
                  </div>
                </article>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          {group.is_member ? (
            <form onSubmit={send} className="border-t border-[#EFE6CF] p-3 flex items-center gap-2 bg-white" data-testid="group-composer">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Post to the group…"
                className="flex-1 bg-[#F9F6EF] border border-[#EFE6CF] text-[#0A1F2E] rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/20"
                data-testid="group-composer-input"
              />
              <button
                type="submit"
                disabled={sending || !draft.trim()}
                className="bg-[#0A1F2E] hover:bg-[#163448] disabled:opacity-50 text-white rounded-full p-2.5 transition"
                data-testid="group-composer-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          ) : (
            <div className="border-t border-[#EFE6CF] p-4 bg-white text-center" data-testid="group-composer-locked">
              <p className="text-sm text-[#0A1F2E]/65 mb-2">Join the group to post.</p>
              <button
                type="button"
                disabled={busyJoin}
                onClick={handleJoin}
                className="inline-flex items-center gap-1.5 bg-[#0A1F2E] hover:bg-[#163448] disabled:opacity-50 text-white font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2 rounded-full transition"
              >
                <UserPlus className="w-3.5 h-3.5" /> Join group
              </button>
            </div>
          )}
        </section>
      </div>
    </PortalLayout>
  );
};

export default PortalPeerCirclesGroupDetail;
